import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {color} from "@khanacademy/wonder-blocks-tokens";
import {
    borderRadius,
    mediaQueries,
    pageHeaderHeight,
} from "@khan/shared-styles";

import styleConstants from "#auth/style-constants.ts";

// Constants used to style a ProgressShell.
// TODO(kairui): Setting absolute heights for these screens is not scalable and
// makes it very difficult to extend the ProgressShell to other use cases.
// Ideally, the content and margins of each screen determines its own height.
// Try to make that happen, please!
export const MIN_SCREEN_HEIGHT = 475;
export const MAX_SCREEN_HEIGHT = 800;
export const DETAIL_SCREEN_HEIGHT_ON_TABLET = 292;
export const DETAIL_SCREEN_HEIGHT_ON_MOBILE = 222;

// Sometimes, we only show one of either the detail or action screen. In which
// case, we can adjust the heights of these screens to be shown in a standalone
// way.
const DETAIL_SCREEN_STANDALONE_HEIGHT = 400;
// Allows entire screen to be visible in landscape view on iPhone 5 (320px), the
// smallest screen height we reasonably support.
const ACTION_SCREEN_STANDALONE_HEIGHT = 320;

type Props = {
    detailScreenNode: React.ReactNode;
    actionScreenNode: React.ReactNode;
    embedded?: boolean;
};

function ProgressShellLayout({
    detailScreenNode,
    actionScreenNode,
    embedded = false,
}: Props): React.ReactElement<React.ComponentProps<"div">> {
    return (
        <div
            className={css(
                styles.progressShell,
                !embedded && styles.standaloneProgressShell,
                styles.progressShellMargin,
            )}
        >
            {detailScreenNode && (
                <section
                    className={css(
                        styles.detailScreen,
                        actionScreenNode
                            ? styles.detailScreenHeight
                            : styles.detailScreenStandaloneHeight,
                        styles.detailScreenMargin,
                    )}
                >
                    {detailScreenNode}
                </section>
            )}
            {actionScreenNode && (
                <section
                    className={css(
                        styles.actionScreenContainer,
                        detailScreenNode
                            ? styles.actionScreenHeight
                            : styles.actionScreenStandaloneHeight,
                        styles.actionScreenMargin,
                    )}
                >
                    {actionScreenNode}
                </section>
            )}
        </div>
    );
}

const styles = StyleSheet.create({
    progressShell: {
        position: "relative",
        flex: 1,
        display: "flex",
        flexDirection: "column",
        [mediaQueries.lgOrLarger]: {
            flexDirection: "row",
        },
        [mediaQueries.xs]: {
            width: "100%",
            minHeight: "100%",
        },
    },
    standaloneProgressShell: {
        maxWidth: `calc(${styleConstants.MAX_SCREEN_WIDTH}px * 2)`,
        width: styleConstants.MAX_SCREEN_WIDTH,
        [mediaQueries.lgOrLarger]: {
            maxHeight: MAX_SCREEN_HEIGHT,
            width: "80%",
        },
    },
    progressShellMargin: {
        [mediaQueries.lgOrLarger]: {
            // Add margin at the bottom so there is space between the
            // progress shell and the footer, which is relevant when
            // the user is using a window with a small height.
            marginBottom: pageHeaderHeight,
        },
    },
    detailScreen: {
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "center",
        width: "100%",
        backgroundColor: color.darkBlue,
        borderRadius: `${borderRadius}px 0 0 ${borderRadius}px`,

        [mediaQueries.mdOrSmaller]: {
            borderRadius: `${borderRadius}px ${borderRadius}px 0 0`,
        },

        [mediaQueries.xs]: {
            marginTop: 0,
            borderRadius: 0,
        },
    },
    detailScreenHeight: {
        [mediaQueries.mdOrSmaller]: {
            height: DETAIL_SCREEN_HEIGHT_ON_TABLET,
            maxHeight: DETAIL_SCREEN_HEIGHT_ON_TABLET,
        },

        [mediaQueries.xs]: {
            height: DETAIL_SCREEN_HEIGHT_ON_MOBILE,
            maxHeight: DETAIL_SCREEN_HEIGHT_ON_MOBILE,
        },
    },
    detailScreenStandaloneHeight: {
        height: DETAIL_SCREEN_STANDALONE_HEIGHT,
    },
    detailScreenMargin: {
        marginTop: 25,
    },
    actionScreenContainer: {
        position: "relative",
        minWidth: styleConstants.MAX_SCREEN_WIDTH,
        backgroundColor: "rgba(255, 255, 255, 0.98)",
        borderRadius: `0 0 ${borderRadius}px ${borderRadius}px`,
        display: "flex",

        [mediaQueries.lgOrLarger]: {
            borderRadius: `0 ${borderRadius}px ${borderRadius}px 0`,
        },
        [mediaQueries.xs]: {
            minWidth: "100%",
            borderRadius: 0,
        },
    },
    actionScreenHeight: {
        minHeight: MIN_SCREEN_HEIGHT,
    },
    actionScreenStandaloneHeight: {
        minHeight: ACTION_SCREEN_STANDALONE_HEIGHT,
    },
    actionScreenMargin: {
        [mediaQueries.lgOrLarger]: {
            marginTop: 25,
        },
    },
});

export default ProgressShellLayout;
